import { debounce, get, isEqual } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  FieldValues,
  Path,
  UseFormGetValues,
  UseFormWatch,
} from "react-hook-form";

import { Schema, SchemaError } from "./types";
import { validateAgainstSchema } from "./validation";

interface Options {
  delay?: number;
  // Checks the schema cannot express, run on the same value after it
  extra?: (value: unknown) => SchemaError[];
}

/*
 * Validates one form field against a JSON Schema whenever it changes,
 * through a watch subscription so the form does not re-render on every keystroke.
 * `validateNow` validates the current value at once, e.g. on submit while a debounced run is pending.
 */
export const useSchemaValidation = <T extends FieldValues>(
  schema: Schema,
  name: Path<T>,
  form: { watch: UseFormWatch<T>; getValues: UseFormGetValues<T> },
  { delay = 300, extra }: Options = {},
) => {
  const { watch, getValues } = form;
  const extraRef = useRef(extra);
  extraRef.current = extra;
  const pending = useRef<{ cancel: () => void }>(null);

  const check = useCallback(
    (value: unknown) => [
      ...validateAgainstSchema(schema, value),
      ...(extraRef.current?.(value) ?? []),
    ],
    [schema],
  );

  const [errors, setErrors] = useState<SchemaError[]>(() =>
    check(getValues(name)),
  );

  // Keeps the same array while the errors do not change, so the form does not re-render
  const publish = useCallback((next: SchemaError[]) => {
    setErrors((previous) => (isEqual(previous, next) ? previous : next));
  }, []);

  useEffect(() => {
    const validate = debounce((value: unknown) => {
      publish(check(value));
    }, delay);
    pending.current = validate;

    validate(getValues(name));

    const subscription = watch((values, { name: changed }) => {
      if (!changed || changed === name || changed.startsWith(`${name}.`)) {
        validate(get(values, name));
      }
    });

    return () => {
      subscription.unsubscribe();
      validate.cancel();
    };
  }, [check, publish, name, watch, getValues, delay]);

  const validateNow = useCallback(() => {
    pending.current?.cancel();
    const next = check(getValues(name));
    publish(next);
    return next.length === 0;
  }, [check, publish, getValues, name]);

  return { errors, isValid: errors.length === 0, validateNow };
};
